import ChatDraw from './Draw';
import ChatAjax from './Ajax';
import ChatPhoto from './Photo';
import ChatSocket from './Socket';
import Store from '../../Common/Store';

/*--------------------------------------
	불러톡 — $j.F.Chat (채팅방/대화창)

	메인 클래스(Chat)가 상태·생명주기를 담당하고, 렌더/통신/미디어/소켓은
	헬퍼 클래스(ChatDraw·ChatAjax·ChatPhoto·ChatSocket)로 분리한다. 헬퍼는 생성 시
	Chat 인스턴스를 받아(this.chat) 상태를 공유한다.

	데이터 흐름
	  1) 최초 로딩 : ajax.getMoreTalk → draw.drawList → draw.talkBox
	  2) 내가 작성 : ajax.sendMessageToGo → /ajax/chat/send → socket.sendToNode → emit
	  3) 수신      : socket 'chat message' → draw.onRecvMsg → draw.drawOne
--------------------------------------*/
export default class Chat {
	constructor(){
		// 컨테이너 주입 규약(다른 모듈과 동일 시그니처 — 현재 미사용)
		this.id = null;
		this.parent = null;
		this.data = null;

		// 헬퍼(initData 에서 생성)
		this.draw = null;
		this.ajax = null;
		this.photo = null;
		this.socket = null;

		// 페이지 주입값(initData 에서 적재)
		this.roomno = null;
		this.myMbno = null;
		this.userNum = null;
		this.roomName = '';
		this.talkName = '';
		this.userName = '';
		this.myIP = '';
		this.isTalkAdmin = 0;
		this.bonsaMap = {};
		this.nameMap = {};
		this.avatarMap = {};      // 보낸이번호→프사 URL 캐시(소켓 수신엔 프사가 없어 과거목록에서 채워 재사용)
		this.chatAbsentMsg = '';
		this.chatHost = '';
		this.isAdminPage = false; // 관리자 채팅창엔 MEMBER 전역이 없음
		this.ns = '';             // EventBus 네임스페이스(G5.NS)

		// 런타임 상태
		this.isMobile = '';
		this.firstRsno = -1;      // g5_chat_messages 의 가장 적은 num(pk)
		this.roomMembers = {};
		this.ongetting = 0;       // 목록 수신중(중복요청 방지)
		this.currentMember = 0;
		this.currentMembersList = '';
		this.uploadMode = 1;      // 1: 텍스트 전송, 2: 파일 업로드
		this.uploadFormData = null;
		this.firstDate = null;    // 화면의 가장 오래된 날짜
		this.lastDate = null;     // 화면의 가장 최근 날짜
		this.observer = null;     // 위로 스크롤 추가로딩 IntersectionObserver
		this.replyTo = null;      // 답장 대상 {num,name,text} (꾹 누르기로 지정)
		this.suppressClick = false; // 롱프레스 직후 따라오는 click 1회 무시
		this.absentSent = false;  // 에티켓 시간 자동답변 — 세션당 1회만 발송
		this._lastTypingAt = 0;   // 입력중(typing) 발신 throttle 타임스탬프
	}

	/**
	 * 입력중(typing) 발신 — 관리자 화면에서만, 1.5초 throttle.
	 */
	maybeEmitTyping(){
		if( !this.isAdminPage ) return;
		const now = Date.now();
		if( now - this._lastTypingAt < 1500 ) return;
		this._lastTypingAt = now;
		this.socket.emitTyping();
	}

	/**
	 * 채팅 화면 실행. 각 사이트의 대화방 페이지 모듈이 부른다 —
	 * Pages/Mobile/Chat(/m/chat) · Pages/MobileAdmin/ChatRoom(/ma/chat/room) · Pages/Admin/ChatPopup(/adm/chat).
	 */
	init(){
		this.initData();
		this.initPage();
		this.initHandler();
		this.socket.connect();
	}

	/**
	 * 정리 — 소켓 종료, 이벤트/옵저버 해제. ($j.destroy / $j.reset 시)
	 */
	destroy(){
		if( this.socket ) this.socket.disconnect();
		if( this.observer ){ this.observer.disconnect(); this.observer = null; }
		if( this.draw ) this.draw.hideTyping(); // 입력중 타이머 정리
		$(document).off('.chat');
		$(window).off('resize.chat');
		if( this.ns ) $j.Common.EventBus.offNamespace(this.ns);
	}

	/**
	 * 대화방 값(blade #chat-data JSON)을 this 로 1회 적재하고 헬퍼를 생성한다.
	 * 값을 만드는 곳은 mobile/pages/chat/chatRoom · adm/pages/chat 의 $chatData.
	 */
	initData(){
		const d = JSON.parse(document.getElementById('chat-data')?.textContent || '{}');
		this.roomno   = d.roomno;
		this.myMbno   = d.userNum;
		this.userNum  = d.userNum;
		this.roomName = d.roomName;
		this.talkName = d.talkName;
		this.userName = d.userName;
		this.myIP     = d.myIP;
		this.isTalkAdmin = d.isTalkAdmin;
		this.bonsaMap = d.bonsaMap || {};
		this.nameMap  = d.nameMap || {}; // 관리자 뷰 전용 표시이름
		// 본인 프사 시드 — 소켓 수신 메시지엔 프사가 없어, 내가 보낸 톡이 첫 줄부터 프사로 보이게 미리 캐시
		if( d.myProfileImg ) this.avatarMap[this.myMbno] = d.myProfileImg;
		this.chatHost = d.chatHost;
		this.chatAbsentMsg = d.chatAbsentMsg || '';
		// 상담원(관리자) 화면인가. /adm 팝업엔 MEMBER 전역이 없어 그걸로 판별해 왔지만,
		// 모바일 관리자(/ma)는 앱 회원세션 위에 얹혀 MEMBER 가 있다 → 페이지가 명시하면(isAdminPage) 그 값이 이긴다.
		this.isAdminPage = (d.isAdminPage != null)
			? !!d.isAdminPage
			: (typeof MEMBER === 'undefined');
		// 관리자가 유저톡(대리로그인)을 모바일 회원화면에서 보는 중 — 아바타/이름을 회원뷰(장비불러 통합)가 아닌 관리자뷰(각자 이름)로 그리기 위함
		this.isChatAdmin = d.isChatAdmin ? 1 : 0;
		// 발신자를 만들 수 없는 대리진입(관리자 앱계정 미연결) — 읽기만 된다. 서버 ChatRoomService::resolveRoom
		this.chatReadonly = d.chatReadonly ? 1 : 0;
		this.ns = G5['NS'];

		this.draw   = new ChatDraw(this);
		this.ajax   = new ChatAjax(this);
		this.photo  = new ChatPhoto(this);
		this.socket = new ChatSocket(this);
	}

	/**
	 * 화면 초기화 — 스크롤 컨테이너 셋업 + 최초 목록 로딩.
	 */
	initPage(){
		dayjs.locale('ko');

		// 관리자 페이지일때 입력창 강조 (배경은 유저창과 동일하게 흰색 유지)
		if( this.isAdminPage ){
			$('#sendmessagcont').css('border', '1px solid black').focus();
		}

		// 추천답변/빠른답변 트레이 + 번개버튼 — 관리자 화면 또는 관리자가 유저톡(대리로그인)을 보는 중일 때만
		const isAdminViewer = this.isAdminPage || !!this.isChatAdmin;
		this.isTrayOn = isAdminViewer;
		this.confirmMsg = '관리자가 확인했습니다'; // 추천답변 확인 = 확인 시스템 라인(이 문구로 발송 → 렌더 시 시스템 라인)
		this.recoReplies = [
			{ label:'네',  content:'네' },
			{ label:'✅ ' + this.confirmMsg, content:this.confirmMsg }, // 라벨엔 체크표시, 발송 내용은 확인문구만
		]; // 추천답변(초록) — 탭 시 즉시 발송
		if( isAdminViewer ){
			$('#chatZapBtn').css('display', 'flex'); // flex 버튼 — show() 는 inline-block 으로 연다
			this.ajax.getKeywords();    // 빠른답변(내 템플릿) 로드
			this.draw.updateRecoTray(); // 추천답변: 마지막이 회원일 때만 노출(메시지 로드 후 재평가)
		}

		// 발신자 없는 대리진입 — 입력 수단을 전부 잠근다. 회원 번호로 보내면 '회원이 한 말'이 되므로
		// 아예 못 쓰게 한다. 대화 읽기는 그대로. (서버 최종 관문은 ChatMessageService::send)
		if( this.chatReadonly ){
			this.isTrayOn = false;
			$('#sendmessagcont').prop('disabled', true)
				.attr('placeholder', '관리자 계정에 연결된 앱 회원이 없어 톡을 보낼 수 없습니다');
			$('.chat-input__send, .chat-input__attach, #chatZapBtn').hide();
		}

		this.draw.setupScroll();
		this.draw.showAbsentBanner(); // 에티켓 시간이면 대화창 위 안내 배너
		this.draw.autoGrow($('#sendmessagcont')[0]); // 입력창 초기 높이 한 줄
		this.ajax.getMoreTalk();
	}

	/**
	 * 이벤트 위임 — blade·생성HTML 클릭을 '.chat' 네임스페이스로 통합.
	 * (재진입 중복 방지: off → on)
	 */
	initHandler(){
		const self = this;
		const Bus = $j.Common.EventBus;
		const $doc = $(document);

		Bus.offNamespace(this.ns); // 재진입 중복 방지

		// 채팅 이미지 업로드 완료(외부 $j.F.Upload 트리거)
		Bus.on(this.ns, 'upload:image', function(e, payload){
			if( $j.F.Upload.getContext() !== 'chatUpload' ) return;
			self.photo.onFileUploaded(payload);
		});

		$doc.off('.chat');

		// 더보기(위로 스크롤 추가로딩은 #top-observer 가 주, 버튼은 보조)
		$doc.on('click.chat', '#getmoreList', () => self.ajax.getMoreTalk());

		// 에티켓 시간 안내 배너 닫기(X) — 닫힘 저장 안 함(다시 열면 재노출)
		$doc.on('click.chat', '.js-chat-absent-close', function(){
			$('#chatAbsentBanner').remove();
			self.draw.resizeScroll(); // 배너 제거분 대화창 높이 복원
		});

		// 번개/삭제 버튼 — 상태에 따라 동작(삭제 > 빠른답변 토글). 관리자만.
		$doc.on('click.chat', '.js-chat-zap', function(){
			const $ta = $('#sendmessagcont');
			if( $.trim($ta.val()).length > 0 ){
				// 삭제(×) — 입력창 비우고 추천답변 재평가(마지막이 회원일 때만 노출)
				$ta.val('');
				self.draw.autoGrow($ta[0]);
				self.draw.showSendBtn();
				self.draw.updateRecoTray();
			} else if( self.draw.trayMode === 'quick' ){
				self.draw.hideTray();        // 빠른답변 닫기
				self.draw.updateRecoTray();  // → 마지막이 회원일 때만 추천답변 노출
			} else {
				self.draw.showQuickTray();   // 빠른답변 열기
			}
		});

		// 추천답변 칩 선택 → 즉시 발송(입력창에 채우지 않음). 누르는 즉시 트레이 숨김.
		$doc.on('click.chat', '.js-chat-send-chip', function(){
			const content = $(this).data('content');
			$('#sendmessagcont').val(content == null ? '' : String(content));
			self.draw.hideTray();      // 누르는 순간 추천창 숨김
			self.socket.sendMessage(); // 일반 전송 경로(방접속 확인 → 저장 → emit)
		});

		// 빠른답변 칩 선택 — 형식에 따라 갈린다.
		//   평문        입력창에 채운다 (고쳐서 보내는 게 대부분이다)
		//   서식글·이미지 입력창에 담을 수 없다 → 미리보기를 거쳐 그대로 보낸다
		$doc.on('click.chat', '.js-chat-chip', function(){
			const i = parseInt($(this).attr('data-i'), 10);
			const k = (self.quickTemplates || [])[i];
			if( !k ) return;
			if( k.body_type === 'text' ) self.fillQuick(k.content);
			else                         self.draw.showQuickPreview(i);
		});

		// 미리보기 — 목록으로 / 이대로 보내기 / 입력창에 넣기
		$doc.on('click.chat', '.js-chat-quick-back', function(){ self.draw.showQuickTray(); });
		$doc.on('click.chat', '.js-chat-quick-fill', function(){
			const k = (self.quickTemplates || [])[parseInt($(this).attr('data-i'), 10)];
			if( k ) self.fillQuick(k.content);
		});
		$doc.on('click.chat', '.js-chat-quick-send', function(){
			const k = (self.quickTemplates || [])[parseInt($(this).attr('data-i'), 10)];
			if( k ) self.ajax.sendQuick(k.num);
		});

		// 첨부(+) 메뉴 토글 (열림 시 버튼 활성)
		$doc.on('click.chat', '.js-chat-attach', function(e){
			e.stopPropagation();
			const open = $('#chatAttachMenu').toggleClass('is-open').hasClass('is-open');
			$(this).toggleClass('is-active', open);
		});

		// 바깥클릭 → 첨부 메뉴 닫기 + 버튼 활성 해제 (+ 노출된 삭제버튼 숨김)
		$doc.on('click.chat', function(){
			$('#chatAttachMenu').removeClass('is-open');
			$('.js-chat-attach').removeClass('is-active');
			if( !self.suppressClick ) self.draw.hideDeleteBtn(); // 롱프레스 직후 click 은 유지
		});

		// 카메라/갤러리 → 업로드
		$doc.on('click.chat', '.js-chat-upload', function(e){
			e.stopPropagation();
			self.photo.startImageUpload($(this).data('uptype'));
		});

		// 전송 버튼
		$doc.on('click.chat', '#send_callbtn', () => self.socket.sendMessage());

		// 입력창 — 전송버튼 토글 + 번개/삭제 상태 + 높이 자동확장
		$doc.on('input.chat change.chat keyup.chat blur.chat', '#sendmessagcont', function(){
			self.draw.showSendBtn();
			self.draw.updateZapBtn();
			self.draw.autoGrow(this);
		});

		// 입력중(typing) 발신 — 관리자끼리만 (throttle 내부처리)
		$doc.on('input.chat', '#sendmessagcont', function(){
			self.maybeEmitTyping();
		});

		// Enter = 줄바꿈, Shift+Enter = 전송
		$doc.on('keydown.chat', '#sendmessagcont', function(e){
			// 한글 등 IME 조합 중 Enter 는 글자 확정용 — 전송하지 않는다
			if( e.isComposing || (e.originalEvent && e.originalEvent.isComposing) || e.keyCode === 229 ) return;
			if( e.key === 'Enter' && e.shiftKey ){
				e.preventDefault(); // 기본 줄바꿈 막고 전송
				self.socket.sendMessage();
			}
			// 그냥 Enter 는 기본 동작(줄바꿈) 유지
		});

		// 드래그 드랍 — 입력창뿐 아니라 채팅 페이지 전체를 드롭존으로.
		// (dragenter/dragover 기본동작을 막아야 drop 발생 + 브라우저 기본 파일열기 차단)
		$doc.on('dragenter.chat dragover.chat', function(e){ e.preventDefault(); e.stopPropagation(); });
		$doc.on('drop.chat', function(e){
			e.preventDefault();
			e.stopPropagation();
			const dt = e.originalEvent.dataTransfer;
			if( dt && dt.files && dt.files.length ) self.photo.readFiles(dt.files);
		});

		// 접어두기/펼쳐보기 토글 (관리자 메시지)
		$doc.on('click.chat', '.js-chat-fold', function(){
			const $b = $(this).closest('.chat-bubble').toggleClass('is-collapsed');
			const collapsed = $b.hasClass('is-collapsed');
			$(this).text(collapsed ? '펼쳐보기' : '접어두기');
			Store.cookie.set('chat_fold_collapsed', collapsed ? '1' : '0', { days: 365 }); // 마지막 선택 기억 → 다음에 열 때 적용
		});

		// ── 관리자 전용: 관리자 메시지 더블클릭 → '삭제' 버튼 → 확인 → 소프트 삭제 ──
		const canDelete = self.isAdminPage || (self.isTalkAdmin == 1) || (self.isChatAdmin == 1);
		if( canDelete ){
			// 더블클릭한 관리자 메시지 왼쪽에 '삭제' 버튼 노출
			$doc.on('dblclick.chat', '.chat-bubble', function(e){
				const $row = $(this).closest('.chat-row');
				if( String($row.data('mgr')) !== '1' ) return;      // 관리자 메시지만 삭제 대상
				if( String($row.data('deleted')) === '1' ) return;  // 이미 삭제됨
				const num = parseInt($row.data('num')) || 0;
				if( !num ) return;
				e.preventDefault();                 // 더블클릭 텍스트 선택 방지
				self.draw.showDeleteBtn($row, num);
			});

			// '삭제' 버튼 클릭 → 확인 다이얼로그 → 예 하면 삭제
			$doc.on('click.chat', '.js-chat-del', function(e){
				e.stopPropagation();               // 바깥클릭(버튼 숨김)으로 전파 방지
				const num = parseInt($(this).data('num')) || 0;
				self.draw.hideDeleteBtn();
				if( num ) self.draw.confirmDeleteMessage(num);
			});

			// ── 회원 메시지 꾹 누르기(롱프레스) → 안읽음 표시 ──
			//  안읽음으로 되돌리면 방목록 뱃지·관리자 알림이 되살아나 "나중에 다시 볼 톡"으로 남는다.
			//  해제는 세 가지 — 태그(안읽음 ✕)를 누르거나, 안읽음인 톡을 다시 꾹 누르거나,
			//  그 방에 답장하면(서버 send) 방 전체가 읽음으로 털린다.
			//  10px 이상 이동(스크롤·드래그선택)에만 취소 — 미세 흔들림은 무시.
			let lpTimer = null, lpX = 0, lpY = 0;
			const lpClear = () => { if( lpTimer ){ clearTimeout(lpTimer); lpTimer = null; } };
			const lpXY = (e) => {
				const t = (e.originalEvent && e.originalEvent.touches && e.originalEvent.touches[0]) || e;
				return { x: t.clientX || 0, y: t.clientY || 0 };
			};
			$doc.on('mousedown.chat touchstart.chat', '.chat-bubble', function(e){
				const $row = $(this).closest('.chat-row');
				if( String($row.data('mgr')) === '1' ) return;      // 회원이 보낸 톡만 대상(관리자 톡은 삭제 동선)
				if( String($row.data('deleted')) === '1' ) return;
				const num = parseInt($row.data('num')) || 0;
				if( !num ) return;
				const p = lpXY(e); lpX = p.x; lpY = p.y;
				lpClear();
				const unread = String($row.attr('data-unread')) === '1'; // 지금 화면에 붙어있는 상태
				lpTimer = setTimeout(() => {
					self.suppressClick = true;                      // 뒤따르는 click(이미지 새창 등) 1회 무시
					self.ajax.setUnread(num, !unread);              // 원하는 상태를 명시(토글 아님)
					setTimeout(() => { self.suppressClick = false; }, 700); // 안전 자동해제
				}, 450);
			});
			$doc.on('mousemove.chat touchmove.chat', '.chat-bubble', function(e){
				if( !lpTimer ) return;
				const p = lpXY(e);
				if( Math.abs(p.x - lpX) > 10 || Math.abs(p.y - lpY) > 10 ) lpClear();
			});
			$doc.on('mouseup.chat mouseleave.chat touchend.chat touchcancel.chat', '.chat-bubble', lpClear);

			// 안읽음 태그(✕) 클릭 → 읽음으로 해제. 말풍선 밖이라 롱프레스와 겹치지 않는다.
			$doc.on('click.chat', '.js-chat-unread', function(e){
				e.stopPropagation();
				const num = parseInt($(this).closest('.chat-row').data('num')) || 0;
				if( num ) self.ajax.setUnread(num, false);
			});
		}

		/* 답장기능 주석처리 — 꾹 누르기(롱프레스)·취소·인용 클릭 비활성화
		// 꾹 누르기(롱프레스) → 답장 대상 지정. 10px 이상 이동(스크롤·드래그선택)에만 취소 — 미세 흔들림은 무시.
		let lpTimer = null, lpX = 0, lpY = 0;
		const lpClear = () => { if( lpTimer ){ clearTimeout(lpTimer); lpTimer = null; } };
		const lpXY = (e) => {
			const t = (e.originalEvent && e.originalEvent.touches && e.originalEvent.touches[0]) || e;
			return { x: t.clientX || 0, y: t.clientY || 0 };
		};
		$doc.on('mousedown.chat touchstart.chat', '.chat-bubble', function(e){
			const $bubble = $(this);
			const p = lpXY(e); lpX = p.x; lpY = p.y;
			lpClear();
			lpTimer = setTimeout(() => {
				self.suppressClick = true;            // 뒤따르는 click(이미지새창 등) 1회 무시
				self.draw.armReply($bubble);
				setTimeout(() => { self.suppressClick = false; }, 700); // 안전 자동해제
			}, 450);
		});
		$doc.on('mousemove.chat touchmove.chat', '.chat-bubble', function(e){
			if( !lpTimer ) return;
			const p = lpXY(e);
			if( Math.abs(p.x - lpX) > 10 || Math.abs(p.y - lpY) > 10 ) lpClear();
		});
		$doc.on('mouseup.chat mouseleave.chat touchend.chat touchcancel.chat', '.chat-bubble', lpClear);

		// 답장 미리보기 취소(×)
		$doc.on('click.chat', '.js-chat-reply-cancel', () => self.draw.cancelReply());

		// 말풍선 인용 칸 클릭 → 원본 메시지로 이동 (롱프레스 직후 click 은 무시)
		$doc.on('click.chat', '.chat-reply-quote', function(){
			if( self.suppressClick ) return;
			self.draw.scrollToMessage($(this).data('ref'));
		});
		*/

		// 생성 HTML(메시지) — 이미지 새창 / 오더수정 (롱프레스 직후 click 은 무시)
		$doc.on('click.chat', '.js-chat-image', function(){ if( self.suppressClick ) return; self.photo.showImage(this); });

		// 카드 클릭 → **그 카드묶음만** 읽는 전용 뷰어. 사진 갤러리(ZoomPhoto)와 섞지 않는다.
		//
		// 그림이 아니라 **카드 전체**가 받는다. 그림에만 걸었더니 '크게보기' 뱃지 위를 누르면
		// 그 아래 슬라이드가 잡혀 아무 일도 안 났다 — 누르라고 적어 둔 자리가 안 눌리는 셈이다.
		$doc.on('click.chat', '[data-cards]', function(e){
			if( self.suppressClick ) return;
			// 넘기는 조작(화살표·점)은 제외한다 — 카드 안에 있어 카드 클릭으로 같이 잡힌다.
			// Swiper 가 만든 버튼이라 전파를 끊어 주지 않으므로 여기서 가른다.
			if( $(e.target).closest('.chat-cards__nav, .swiper-pagination').length ) return;
			e.stopPropagation();   // 대화창 바탕 클릭으로 번지지 않게
			const $c = $(this);
			let items = [];
			try { items = JSON.parse($c.attr('data-cards') || '[]'); } catch(err) {}
			if( !items.length ) return;
			// 말풍선은 x700 리사이즈본으로 그린다(대화창 폭이면 충분하다). 확대는 **원본**이다 —
			// 카드뉴스는 글이 그림 안에 있어 리사이즈본으로 키우면 작은 글씨가 뭉갠다.
			// 변환은 사진 갤러리(F/Chat/Photo.showImage)가 쓰는 것과 같은 규칙이다.
			items = items.map(it => ({ url: $j.Common.Util.toOriginalUrl(it.url) }));
			// 지금 보고 있던 장에서 이어 읽는다
			const $active = $c.find('.swiper-slide-active');
			const index = $active.length ? $active.index() : 0;
			$j.Ui.CardNews.open({ items, index, title: '' });
		});
		$doc.on('click.chat', '.js-chat-order', function(){
			if( self.suppressClick ) return;
			$j.F.Admin.Util.userBal($(this).data('mbno'), $(this).data('ordernum'));
		});
		// 관리자: 회원(고객) 아바타 클릭 → 기사관리 포털 (chat/room 목록과 동일).
		// PC 관리자(/adm)에서만 별창 포털을 연다 — 폰(/ma)에서는 PC 팝업이 무의미하므로 무동작.
		// (구엔 admin.js memberPortalWin 의 존재 여부로 갈랐다 — 그게 곧 /adm 이었다)
		$doc.on('click.chat', '.chat-avatar--user', function(){
			if( self.suppressClick ) return;
			if( !$j.Common.Util.isMobile() ) $j.F.Admin.Util.memberPortal($(this).data('mbno'), '', true);
		});
	}

	/** 빠른답변 글을 입력창에 채운다 — 고쳐서 보내라는 뜻이라 발송하지 않는다 */
	fillQuick(content){
		const $ta = $('#sendmessagcont');
		$ta.val(content == null ? '' : String(content));
		this.draw.hideTray();
		this.draw.showSendBtn();
		this.draw.autoGrow($ta[0]);
		this.draw.updateZapBtn();
		$ta.focus();
	}

}

// 앱이 직접 호출하던 전역 진입점 3종(device_backButtonCall / checkChatMessage / rcgback_image_upload)은
// 2026-08 제거. 이미지 업로드 완료는 broker 콜백 callbackUploadImage → EventBus 'upload:image' 로 받고
// (위 initHandler 구독자), 나머지 2종은 앱 발화가 이미 없어진 죽은 진입점이었다.